import { Box, Stack, Typography, TypographyProps } from '@mui/material';
import { isBefore } from 'date-fns';
import { maxBy, size } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDevice } from '@/api/devices';
import {
    WaveSpectrumDatum,
    useEstimatedWaveEnergySpectrum,
    useWaveSpectrumDataPage,
    useWaveSpectrumSample,
} from '@/api/historicalData';
import waveSpreadIcon from '@/assets/WaveSpreadIcon.svg';
import { radialSpectrumChart } from '@/charts';
import { captureChartTeardown } from '@/charts/common';
import { convert } from '@/common/convert';
import { Datum, renderUnit } from '@/components/data/Datum';
import { SensorIcon } from '@/components/data/SensorIcon';
import { Timestamp } from '@/components/data/Timestamp';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Spacer } from '@/components/util';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePreferredUnits } from '@/hooks/usePreferredUnits';
import { boldWeight, minorTextGrey } from '@/theme';

export const frequencyBinWidth = 0.00977;
/**
 * If energy level at any given direction*frequency is below this value in m^2/(Hz * deg), we don't render it.
 */
export const IGNORE_ENERGY_BELOW_M = 0.0001;

const config = {
    radiusKey: 'freq',
    angleKey: 'dir',
    colorKey: 'energy',
    legendLabel: 'Energy',
    radiusBinWidth: frequencyBinWidth,
};

export const EstimatedWaveSpectrumSection = () => {
    const { viaOceansApi } = useSpotterContext();
    const { data: device } = useDevice({ config: { enabled: !viaOceansApi } });
    const spotterId = device?.spotterId ?? '';
    const { selectedTimestamp } = useTimelineNavigationContext();
    const { dateRange } = useDateRangeContext();
    const [hasAnySpectrum, setHasAnySpectrum] = useState(false);

    // fetch some wave samples around the end of our window to see if there's any data so we can decide whether or not to show a loading indicator
    // we'll try to load data for the current timestamp either way, just in case, but no reason to tell people to select a time to see spectrum
    // data if we don't think anything's likely to be there.
    const sentinelTimestamp = useMemo(() => {
        if (!device?.timeLastUpdatedUTC) {
            return dateRange?.endDate;
        }
        const lastUpdated = new Date(device.timeLastUpdatedUTC);
        if (!dateRange?.endDate || isBefore(lastUpdated, dateRange.endDate)) {
            return lastUpdated;
        }
        return dateRange.endDate;
    }, [dateRange?.endDate, device?.timeLastUpdatedUTC]);

    const { data: waveSpectrumPage } = useWaveSpectrumDataPage({
        spotterId,
        timestamp: sentinelTimestamp,
    });

    const waveSpectrumSampleQuery = useWaveSpectrumSample({
        spotterId,
        timestamp: selectedTimestamp ? new Date(selectedTimestamp) : undefined,
        samplePeriodHours: device?.currentMessagePeriod ?? 1,
    });

    useEffect(() => {
        if (hasAnySpectrum) {
            return;
        }
        if (waveSpectrumSampleQuery.data?.waveSpectrumSample || size(waveSpectrumPage)) {
            setHasAnySpectrum(true);
        }
    }, [hasAnySpectrum, waveSpectrumPage, waveSpectrumSampleQuery.data?.waveSpectrumSample]);

    if (!hasAnySpectrum) {
        // if the page around the endDate has no spectrum samples, and we've never seen one on any other selected timestamp,
        // don't distract with an eternally empty chart
        return false;
    }

    return <EstimatedWaveSpectrumChart spotterId={spotterId} waveSpectrumSampleQuery={waveSpectrumSampleQuery} />;
};

const DEFAULT_MAX_FREQUENCY = 0.3;

export const EstimatedWaveSpectrumChart = ({
    spotterId,
    waveSpectrumSampleQuery,
}: {
    spotterId: string;
    waveSpectrumSampleQuery: ReturnType<typeof useWaveSpectrumSample>;
}) => {
    const { selectedTimestamp } = useTimelineNavigationContext();
    const preferredUnit = usePreferredUnits('directionalWaveEnergy');
    const { isPending: isSamplePending, data: { timestamp: sampleTimestamp, waveSpectrumSample } = {} } =
        waveSpectrumSampleQuery;

    const chartCursorsFlag = useFeatureFlag('waveSpectrumChartCursors');
    const { data: estimatedWaveSpectrum, isFetching: isSpectrumCalculating } = useEstimatedWaveEnergySpectrum({
        spotterId,
        payload: waveSpectrumSample!,
        timestamp: sampleTimestamp ?? '',
        config: {
            enabled: Boolean(sampleTimestamp && waveSpectrumSample),
        },
    });
    const [maxFrequency, setMaxFrequency] = useState(DEFAULT_MAX_FREQUENCY);
    const [maxEnergy, setMaxEnergy] = useState<number>();
    const displayTimestamp = sampleTimestamp ?? selectedTimestamp;
    const containerRef = useRef<HTMLDivElement>(null);
    const { width } = useElementSize(containerRef, { responsive: true, wait: 150 });
    const containerId = 'estimated-wave-spectrum-chart';
    const drawChart = useCallback(radialSpectrumChart, []);

    const isLoading = Boolean(displayTimestamp && (isSpectrumCalculating || isSamplePending));
    const ignoreValuesBelow = convert(IGNORE_ENERGY_BELOW_M).from('m^2/(Hz*Deg)').to(preferredUnit);

    const sampleMaxEnergy = useMemo(() => maxBy(estimatedWaveSpectrum, 'energy')?.energy ?? 0, [estimatedWaveSpectrum]);
    const sampleMaxFrequency = useMemo(
        () =>
            maxBy(
                estimatedWaveSpectrum?.filter(({ energy }) => energy >= ignoreValuesBelow),
                'freq'
            )?.freq ?? 0,
        [estimatedWaveSpectrum, ignoreValuesBelow]
    );
    const allEnergyBelowThreshold = sampleMaxEnergy < ignoreValuesBelow;
    const defaultMaxEnergy = preferredUnit === 'cm^2/(Hz*Deg)' ? 200 : 30;

    useEffect(() => {
        // only update the scales if we have data, otherwise the scales keep jumping back to the defaults while data is loading
        // if we want to retain scales during navigation, this would be the place to do that instead, but so far we believe seeing
        // the shape of the spectrum with good resolution throughout the domain is more important than consistent scales between samples.
        if (sampleMaxFrequency) {
            setMaxFrequency(Math.max(sampleMaxFrequency, DEFAULT_MAX_FREQUENCY));
        }
        if (sampleMaxEnergy) {
            setMaxEnergy(Math.max(sampleMaxEnergy, defaultMaxEnergy));
        }
    }, [sampleMaxEnergy, sampleMaxFrequency, defaultMaxEnergy]);

    useEffect(() => {
        drawChart<WaveSpectrumDatum>({
            dataset: estimatedWaveSpectrum?.length ? estimatedWaveSpectrum : [],
            containerId,
            config: {
                ...config,
                maxFrequency,
                maxEnergy: maxEnergy ?? defaultMaxEnergy,
                width,
                showHoverCursors: chartCursorsFlag,
                legendUnits: renderUnit(preferredUnit),
                ignoreValuesBelow,
            },
        });

        return captureChartTeardown(containerId);
    }, [
        chartCursorsFlag,
        defaultMaxEnergy,
        drawChart,
        estimatedWaveSpectrum,
        ignoreValuesBelow,
        maxEnergy,
        maxFrequency,
        preferredUnit,
        width,
    ]);

    return (
        <Stack sx={{ width: 1, containerType: 'inline-size', position: 'relative' }}>
            <SectionHeader
                title="Wave spectrum"
                icon={<SensorIcon src={waveSpreadIcon} />}
                isLoading={isLoading}
                sx={{ width: 1, flexWrap: 'wrap', display: { '@150': 'flex', '@': 'none' } }}
            >
                <Stack sx={{ flexDirection: 'row', flexGrow: 1 }}>
                    <Spacer />
                    {displayTimestamp && (
                        <Timestamp
                            variant="caption"
                            sx={{ color: minorTextGrey }}
                            value={displayTimestamp}
                            type="short"
                        />
                    )}
                </Stack>
            </SectionHeader>
            <Box id={containerId} ref={containerRef} sx={{ width: 1, overflow: 'hidden' }}></Box>
            {estimatedWaveSpectrum && allEnergyBelowThreshold && (
                <RadialChartNoDataMessage>
                    Wave energy below
                    <br />
                    <Datum
                        variant="inherit"
                        value={IGNORE_ENERGY_BELOW_M}
                        type="directionalWaveEnergy"
                        sourceUnit="m^2/(Hz*Deg)"
                    />
                </RadialChartNoDataMessage>
            )}
            {!isLoading && !estimatedWaveSpectrum && displayTimestamp && (
                <RadialChartNoDataMessage>
                    No wave spectrum
                    <br />
                    data available
                </RadialChartNoDataMessage>
            )}
            {!estimatedWaveSpectrum && !displayTimestamp && (
                <RadialChartNoDataMessage>
                    Select a data point
                    <br />
                    to view wave spectrum
                </RadialChartNoDataMessage>
            )}
        </Stack>
    );
};

const RadialChartNoDataMessage = ({ sx, ...props }: TypographyProps) => (
    <Typography
        variant="minorLabel"
        sx={{
            fontWeight: boldWeight,
            textAlign: 'center',
            position: 'absolute',
            left: '50%',
            top: 'calc(50% - 40px)',
            transform: 'translate(-50%, -50%)',
            ...sx,
        }}
        {...props}
    />
);
